import { useState, useRef, useCallback, useEffect } from 'react';
import { Menu, MenuItem } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MenuIcon from '@mui/icons-material/Menu';
import MinimizeIcon from '@mui/icons-material/Minimize';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';

import JxIconButton from '../../../JxControls/JxButtons/JxIconButton.jsx';
import JxCheckbox from '../../../JxControls/JxToggles/JxCheckbox.jsx';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';

const MENU_ITEM_SX = {
    fontSize: 13,
    color: '#e8edf5',
    gap: '6px',
    '&:hover': { background: 'rgba(102, 178, 255, 0.15)' },
};

/**
 * UmlCanvasTitlebar — canvas-toolbar-specific titlebar: drag grip on the RIGHT
 * (next to a pin toggle) instead of the left, hamburger menu on the left
 * (Save schema note, Hide Selected row, Hide Hover row). Kept separate from
 * the shared JxTitleBar (used by UmlSchemaConfigDlg, UmlWorldMap, etc.) so
 * this toolbar-specific layout can never affect other JxForm dialogs. Reuses
 * JxTitleBar's CSS classes for the same visual language.
 *
 * Pure presentational component — all state (isPinned, hideSelectedRow,
 * hideHoverRow) lives in UmlCanvasToolbar, passed down as props + callbacks.
 * @param {string} id
 * @param {string} title
 * @param {object} dragHandleProps - from JxFormDraggableBase.dragHandleProps
 * @param {Function} onSave
 * @param {boolean} hideSelectedRow
 * @param {Function} onToggleHideSelectedRow
 * @param {boolean} hideHoverRow
 * @param {Function} onToggleHideHoverRow
 * @param {boolean} isPinned
 * @param {Function} onTogglePin
 * @param {boolean} [isMinimizable] - shows a minimize icon button, same contract as JxTitleBar:
 *   fires ComponentNotifier.notify('minimize', windowId) on click, no injected callback.
 * @param {string} [windowId] - required when isMinimizable is true.
 */
export default function UmlCanvasTitlebar({
    id,
    title,
    dragHandleProps,
    onSave,
    hideSelectedRow,
    onToggleHideSelectedRow,
    hideHoverRow,
    onToggleHideHoverRow,
    isPinned,
    onTogglePin,
    onCloseWorkspace,
    isMinimizable = false,
    windowId,
}) {
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const paperRef = useRef(null);

    const handleMinimizeClick = (e) => {
        e.stopPropagation();
        ComponentNotifier.notify('minimize', windowId);
    };

    const handleMenuOpen = useCallback(function openMenu(e) {
        e.stopPropagation();
        setMenuAnchorEl(e.currentTarget);
    }, []);

    const handleMenuClose = useCallback(function closeMenu() {
        setMenuAnchorEl(null);
    }, []);

    const handleSaveClick = useCallback(
        function saveClick() {
            setMenuAnchorEl(null);
            onSave?.();
        },
        [onSave],
    );

    // Same workaround as UmlIconWidget — MUI's own outside-click/backdrop detection
    // isn't reliable here (heavy custom drag/pointer handling on the panel itself).
    useEffect(
        function manageOutsideClick() {
            if (!menuAnchorEl) return undefined;

            function handleDocumentMouseDown(e) {
                if (paperRef.current && paperRef.current.contains(e.target)) return;
                setMenuAnchorEl(null);
            }

            document.addEventListener('mousedown', handleDocumentMouseDown, true);
            return function cleanup() {
                document.removeEventListener('mousedown', handleDocumentMouseDown, true);
            };
        },
        [menuAnchorEl],
    );

    return (
        <div id={id} className="jx-titlebar jx-titlebar-toolwindow jx-titlebar-draggable" {...dragHandleProps}>
            <div className="jx-titlebar-left">
                <JxIconButton icon={<MenuIcon sx={{ fontSize: 16 }} />} onClick={handleMenuOpen} />
                <span className="jx-titlebar-label" style={{ textTransform: 'none' }}>
                    {title}
                </span>
            </div>
            <div className="jx-titlebar-actions">
                {isMinimizable && (
                    <JxIconButton icon={<MinimizeIcon sx={{ fontSize: 16 }} />} onClick={handleMinimizeClick} />
                )}
                <JxIconButton
                    icon={isPinned ? <PushPinIcon sx={{ fontSize: 16 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 16 }} />}
                    onClick={onTogglePin}
                />
                <DragIndicatorIcon className="jx-titlebar-grip" sx={{ fontSize: 16 }} />
            </div>

            <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                sx={{ zIndex: 20000 }}
                slotProps={{
                    paper: {
                        ref: paperRef,
                        onMouseDown: (e) => e.stopPropagation(),
                        sx: { background: '#1c2b3f', border: '1px solid #3a4a63', color: '#e8edf5' },
                    },
                }}
            >
                <MenuItem
                    onClick={() => {
                        handleMenuClose();
                        onCloseWorkspace();
                    }}
                    sx={MENU_ITEM_SX}
                >
                    Close workspace
                </MenuItem>

                <MenuItem onClick={handleSaveClick} sx={MENU_ITEM_SX}>
                    Save schema note
                </MenuItem>
                <MenuItem disableRipple sx={MENU_ITEM_SX}>
                    <JxCheckbox
                        id="uct_hideSelectedRow"
                        checked={hideSelectedRow}
                        onChange={onToggleHideSelectedRow}
                        label="Hide Selected row"
                    />
                </MenuItem>
                <MenuItem disableRipple sx={MENU_ITEM_SX}>
                    <JxCheckbox id="uct_hideHoverRow" checked={hideHoverRow} onChange={onToggleHideHoverRow} label="Hide Hover row" />
                </MenuItem>
            </Menu>
        </div>
    );
}
